import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/common/DataTable';
import StatusBadge from '../../components/common/StatusBadge';
import { usePagedData } from '../../hooks/usePagedData';
import { purchaseOrdersApi } from '../../api/endpoints';
import { PurchaseOrderList, PurchaseOrderStatusMap, ChannelMap } from '../../types';
import Select from '../../components/common/Select';
import { FiRefreshCw, FiSearch, FiCheckCircle, FiDownload, FiX } from 'react-icons/fi';
import { SkeletonTable } from '../../components/common/Skeleton';
import { exportApi } from '../../api/endpoints';
import { downloadExcel } from '../../utils/downloadFile';
import { toast } from 'react-toastify';

const PurchaseOrdersPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0 });

  const {
    data,
    loading,
    query,
    filters,
    setFilters,
    setPage,
    setPageSize,
    setSort,
    setSearch,
    refresh,
  } = usePagedData<PurchaseOrderList>(purchaseOrdersApi.list, {
    initialSortBy: 'orderDate',
    initialSortDesc: true,
  });

  const handleSearchSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      setSearch(searchInput);
    },
    [searchInput, setSearch]
  );

  const handleStatusFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        status: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleChannelFilter = useCallback(
    (value: string) => {
      setFilters((prev: Record<string, any>) => ({
        ...prev,
        channel: value === '' ? undefined : Number(value),
      }));
    },
    [setFilters]
  );

  const handleSync = useCallback(async () => {
    setSyncing(true);
    try {
      await purchaseOrdersApi.sync();
      toast.success('Synchronizacja zamówień rozpoczęta');
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd synchronizacji');
    } finally {
      setSyncing(false);
    }
  }, [refresh]);

  const handleRowClick = useCallback(
    (row: PurchaseOrderList) => {
      navigate(`/purchase-orders/${row.id}`);
    },
    [navigate]
  );

  const handleBulkConfirm = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setBulkProcessing(true);
    setBulkProgress({ current: 0, total: selectedIds.size });
    try {
      const result = await purchaseOrdersApi.bulkConfirm(Array.from(selectedIds));
      const r = result.data;
      setBulkProgress({ current: r.total, total: r.total });
      toast.success(`Potwierdzono ${r.success} z ${r.total} zamówień`);
      if (r.success < r.total) {
        const errors = r.results.filter((x: any) => x.status === 'error');
        errors.forEach((e: any) => toast.error(`PO ${e.id.substring(0, 8)}: ${e.message}`));
      }
      setSelectedIds(new Set());
      refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Błąd bulk potwierdzenia');
    } finally {
      setBulkProcessing(false);
    }
  }, [selectedIds, refresh]);

  const handleExportCsv = useCallback(async () => {
    try {
      const response = await purchaseOrdersApi.exportCsv(filters);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `purchase_orders_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV wyeksportowany');
    } catch (error: any) {
      toast.error('Błąd eksportu CSV');
    }
  }, [filters]);

  const formatCurrency = (amount: number) =>
    amount.toLocaleString('pl-PL', { style: 'currency', currency: 'EUR' });

  const columns = [
    {
      key: 'amazonPurchaseOrderNumber',
      header: 'Numer PO',
      sortable: true,
    },
    {
      key: 'channel',
      header: 'Kanał',
      render: (row: PurchaseOrderList) => (
        <StatusBadge type="channel" value={row.channel} />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (row: PurchaseOrderList) => (
        <StatusBadge type="order" value={row.status} />
      ),
    },
    {
      key: 'orderDate',
      header: 'Data zamówienia',
      sortable: true,
      render: (row: PurchaseOrderList) =>
        new Date(row.orderDate).toLocaleDateString('pl-PL'),
    },
    {
      key: 'totalAmount',
      header: 'Kwota',
      sortable: true,
      render: (row: PurchaseOrderList) => formatCurrency(row.totalAmount),
    },
    {
      key: 'itemCount',
      header: 'Pozycje',
      render: (row: PurchaseOrderList) => row.itemCount,
    },
  ];

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Zamówienia zakupu</h1>
        <div className="page-actions">
          <button className="btn btn-secondary" onClick={handleExportCsv}>
            <FiDownload /> CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => downloadExcel(exportApi.purchaseOrdersXlsx, `zamowienia_${new Date().toISOString().slice(0,10)}.xlsx`)}
          >
            <FiDownload /> Excel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSync}
            disabled={syncing}
          >
            <FiRefreshCw className={syncing ? 'spin' : ''} />
            {syncing ? 'Synchronizacja...' : 'Synchronizuj z Amazon'}
          </button>
        </div>
      </div>

      {/* Bulk actions bar */}
      {selectedIds.size > 0 && (
        <div className="bulk-actions-bar">
          <span style={{ fontWeight: 600 }}>
            Zaznaczono: {selectedIds.size} {selectedIds.size === 1 ? 'zamówienie' : 'zamówień'}
          </span>
          {bulkProcessing && (
            <div style={{ flex: 1, maxWidth: '200px' }}>
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${bulkProgress.total > 0 ? (bulkProgress.current / bulkProgress.total) * 100 : 0}%` }}
                />
              </div>
              <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                {bulkProgress.current}/{bulkProgress.total}
              </span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleBulkConfirm}
              disabled={bulkProcessing}
            >
              <FiCheckCircle /> {bulkProcessing ? 'Potwierdzam...' : 'Potwierdź zaznaczone'}
            </button>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkProcessing}
            >
              <FiX /> Odznacz
            </button>
          </div>
        </div>
      )}

      <div className="toolbar">
        <form className="toolbar-search" onSubmit={handleSearchSubmit}>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              placeholder="Szukaj zamówień..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn btn-secondary">
              <FiSearch />
            </button>
          </div>
        </form>

        <div className="toolbar-filters">
          <Select
            options={[
              { value: '', label: 'Wszystkie statusy' },
              ...Object.entries(PurchaseOrderStatusMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.status ?? '')}
            onChange={handleStatusFilter}
            placeholder="Wszystkie statusy"
          />

          <Select
            options={[
              { value: '', label: 'Wszystkie kanały' },
              ...Object.entries(ChannelMap).map(([key, label]) => ({ value: key, label })),
            ]}
            value={String(filters.channel ?? '')}
            onChange={handleChannelFilter}
            placeholder="Wszystkie kanały"
          />
        </div>
      </div>

      {loading ? (
        <SkeletonTable rows={8} cols={7} />
      ) : (
        <DataTable
          columns={columns}
          data={data?.items || []}
          totalCount={data?.totalCount || 0}
          page={query.page}
          pageSize={query.pageSize}
          totalPages={data?.totalPages || 0}
          sortBy={query.sortBy}
          sortDescending={query.sortDescending}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSort={setSort}
          onRowClick={handleRowClick}
          selectable
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      )}
    </div>
  );
};

export default PurchaseOrdersPage;
